import React, { useEffect, useMemo, useState } from "react";
import { Box, Dialog, DialogContent, IconButton, Modal } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import { FaWallet } from "react-icons/fa";
import moment from "moment";
import AssignUtils from "../../assigned-module/services/AssignUtils";
import transactionFilter from "../../../../../assets/icons/transactionFilter.svg";
import "../../assigned-module/styles/createAccountStyles.css";

const assignUtils = new AssignUtils();
const TXN_PAGE_SIZE = 30;
const BLOCKED_PAGE_SIZE = 30;

/**
 * Self-contained wallet-details dialog for the claim-details (AI review) screen.
 * Mirrors the plans-screen wallet dialog (AssignedPlansOnPid) — same wallet APIs
 * and the same createAccountStyles.css classes — but fetches its own data from
 * (patientId, planId, walletId) so it can be dropped onto the claim screen.
 *
 * Balance + sub-limits need the plan-assignment id, which the claim screen
 * doesn't carry, so it's resolved by matching planId in the patient's plans.
 * Transactions + blocked amount only need walletId (which the claim has).
 */
function WalletDetailsDialog({
  open,
  onClose,
  patientId,
  planId,
  walletId,
  patientName,
  accountName,
}) {
  const [subLimitData, setSubLimitData] = useState({});
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [tab, setTab] = useState(0);

  const [walletTrans, setWalletTrans] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [txnCurrentPage, setTxnCurrentPage] = useState(1);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [totalBlocked, setTotalBlocked] = useState(0);

  const [blockedAmount, setBlockedAmount] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});

  const fetchTransactions = (page) => {
    assignUtils.getWalletData(
      { walletId: walletId, range: "all", pageNum: page, pageSize: TXN_PAGE_SIZE },
      (res) => {
        setWalletTrans((res && res.transactions) || []);
        setTxnTotalPages(
          Math.ceil(((res && res.totalCount) || 0) / TXN_PAGE_SIZE) || 1
        );
        setTxnCurrentPage(page);
      },
      () => {}
    );
  };

  const fetchBlocked = (page) => {
    assignUtils.getBlockedAmount(
      patientId,
      { walletId: walletId, range: "", pageNo: page, pageSize: BLOCKED_PAGE_SIZE },
      (res) => {
        setBlockedAmount((res && res.entries) || []);
        setTotalPages(
          res && res.totalCount > 0
            ? Math.ceil(res.totalCount / BLOCKED_PAGE_SIZE)
            : 1
        );
        setCurrentPage(page);
      },
      () => {}
    );
  };

  const fetchWalletBalance = () => {
    if (!patientId || !planId) return;
    assignUtils.getPlansForPid2(
      patientId,
      (plans) => {
        const list = Array.isArray(plans) ? plans : [];
        const match = list.find(
          (p) =>
            p &&
            p.patientPlanInfo &&
            String(p.patientPlanInfo.planId) === String(planId)
        );
        if (!match) return;
        setIsUnlimited(
          Boolean(
            match.planDetails &&
              match.planDetails.walletDetails &&
              match.planDetails.walletDetails.isUnlimitedWallet
          )
        );
        const aid = match.patientPlanInfo.planAssignmentId;
        if (aid == null) return;
        assignUtils.getWalletAmount(
          planId,
          patientId,
          aid,
          (res) => {
            if (res) {
              setSubLimitData(res);
              setTotalBlocked((res.wallet && res.wallet.blockedAmount) || 0);
            }
          },
          () => {}
        );
      },
      () => {}
    );
  };

  useEffect(() => {
    if (!open || !walletId) return;
    setTab(0);
    setSelectedFilter("all");
    setSubLimitData({});
    fetchTransactions(1);
    fetchBlocked(1);
    fetchWalletBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, walletId]);

  const filteredTransactions = useMemo(() => {
    const list = Array.isArray(walletTrans) ? walletTrans : [];
    const pastDate = (monthsAgo) => {
      const d = new Date();
      d.setMonth(d.getMonth() - monthsAgo);
      return d;
    };
    return list.filter((t) => {
      const dt = new Date(t.createdAt);
      if (Number.isNaN(dt.getTime())) return false;
      switch (selectedFilter) {
        case "3months":
          return dt >= pastDate(3);
        case "6months":
          return dt >= pastDate(6);
        case "1year":
          return dt >= pastDate(12);
        default:
          return true;
      }
    });
  }, [walletTrans, selectedFilter]);

  const handleTxnPageChange = (page) => {
    if (page < 1 || page > txnTotalPages) return;
    fetchTransactions(page);
  };
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchBlocked(page);
  };
  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const sortedTransactions = filteredTransactions
    .slice()
    .sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  const sortedBlocked = (Array.isArray(blockedAmount) ? blockedAmount : [])
    .slice()
    .sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <h2 className="wallet-dialog-title">Wallet details</h2>

      <DialogContent>
        {/* Wallet Card */}
        {subLimitData && subLimitData.wallet && (
          <Box className="wallet-box">
            <div className="wallet-header">
              <div>
                <p className="wallet-patient-name">{patientName}</p>
                <p className="wallet-subtitle">{accountName}</p>
              </div>

              <div className="wallet-right">
                <div className="wallet-title">
                  <FaWallet className="wallet-icon" /> Wallet
                </div>
                <p className="wallet-expiry">
                  Valid upto -{" "}
                  {moment.utc(subLimitData.wallet.expiryDate).format("DD MMM YYYY")}
                </p>
              </div>
            </div>

            {!isUnlimited && (
              <div className="wallet-body">
                <div className="wallet-body-box">
                  <div className="wallet-amount-row">
                    <div>
                      <p className="wallet-label">Balance</p>
                      <p className="wallet-amount">₹{subLimitData.wallet.amount}</p>
                    </div>
                    <div>
                      <p className="wallet-label">Total wallet size</p>
                      <p className="wallet-total">₹{subLimitData.wallet.total}</p>
                    </div>
                  </div>

                  <div className="wallet-progress-bar">
                    <div
                      className="wallet-progress"
                      style={{
                        "--progress-width": `${
                          (subLimitData.wallet.amount /
                            (subLimitData.wallet.total || 1)) *
                          100
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </Box>
        )}

        {/* Tabs */}
        <div className="wallet-tabs">
          <div
            className={`wallet-tab ${tab === 0 ? "active" : ""}`}
            onClick={() => setTab(0)}
          >
            Wallet sub-limits
          </div>
          <div
            className={`wallet-tab ${tab === 1 ? "active" : ""}`}
            onClick={() => setTab(1)}
          >
            Wallet transaction history
          </div>
          <div
            className={`wallet-tab ${tab === 2 ? "active" : ""}`}
            onClick={() => setTab(2)}
          >
            Blocked amount
          </div>
        </div>

        {tab === 0 && (
          <Box className="mt-3 wallet-section">
            {subLimitData && subLimitData.sublimit && subLimitData.sublimit.length > 0 ? (
              subLimitData.sublimit.map((item, index) => (
                <div key={index} className="wallet-sublimit-card">
                  <h4 className="wallet-title">{item.name}</h4>

                  <div className="wallet-tags">
                    {item.isCashless && (
                      <span className="wallet-tag orange">Cashless</span>
                    )}
                    {item.isClaim && (
                      <span className="wallet-tag purple">Claim</span>
                    )}
                  </div>
                  <div
                    className={
                      item.status.statusId === 1 &&
                      item.balance?.total === 0 &&
                      item.balance?.available === 0
                        ? ""
                        : `wallet-limit-box ${
                            [2, 3].includes(item.status.statusId)
                              ? "wallet-greyed"
                              : ""
                          }`
                    }
                  >
                    <div className="flex justify-between items-center">
                      <p
                        className={`limit-label ${
                          [2, 3].includes(item.status.statusId) &&
                          !(
                            item.status.statusId === 1 &&
                            item.balance?.total === 0 &&
                            item.balance?.available === 0
                          )
                            ? "status-red"
                            : ""
                        }`}
                      >
                        {item.status.statusId === 1 &&
                        item.balance?.total === 0 &&
                        item.balance?.available === 0
                          ? "*no sublimit exist"
                          : item.status.statusMsg}
                      </p>

                      {item.balance.isFixedAmount && (
                        <span
                          className="wallet-fixed"
                          style={{ marginLeft: "262px" }}
                        >
                          <i className="fa fa-lock"></i> Fixed sublimit
                        </span>
                      )}
                    </div>

                    {!(
                      item.status.statusId === 1 &&
                      item.balance?.total === 0 &&
                      item.balance?.available === 0
                    ) && (
                      <>
                        <div className="limit-row">
                          <p className="available">
                            ₹{item.balance.available}
                            {item.balance.blocked > 0 && (
                              <IconButton
                                onClick={() => handleOpen(item)}
                                className="info-icon"
                                size="small"
                              >
                                <InfoOutlinedIcon
                                  style={{ color: "#F36B2B" }}
                                />
                              </IconButton>
                            )}
                          </p>
                          <p className="available">₹{item.balance.total}</p>
                        </div>

                        <div className="wallet-sublimit-bar">
                          <div
                            className="wallet-sublimit-progress"
                            style={{
                              width: `${
                                (item.balance.available /
                                  (item.balance.total || 1)) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {(() => {
                    const cashlessPerTxn = item.cashless?.perTransactionlimit;
                    const claimPerTxn = item.claim?.perTransactionlimit;

                    const cashlessCoPay = item.cashless?.coPaylimit;
                    const claimCoPay = item.claim?.coPaylimit;

                    const formatValue = (limit) =>
                      limit.unit === 1 ? `${limit.value}%` : `₹${limit.value}`;

                    const renderLimit = (cashless, claim, label) => {
                      if (!(cashless?.enabled || claim?.enabled)) return null;

                      const cashlessValue = cashless?.enabled
                        ? formatValue(cashless)
                        : null;
                      const claimValue = claim?.enabled
                        ? formatValue(claim)
                        : null;

                      if (cashlessValue && claimValue) {
                        if (cashlessValue === claimValue) {
                          return (
                            <div className="wallet-detail">
                              {label} - {cashlessValue}
                            </div>
                          );
                        }
                        return (
                          <div className="wallet-detail">
                            {label} - {cashlessValue} (Cashless), {claimValue}{" "}
                            (Claim)
                          </div>
                        );
                      }

                      if (cashlessValue) {
                        return (
                          <div className="wallet-detail">
                            {label} - {cashlessValue} (Cashless)
                          </div>
                        );
                      }

                      if (claimValue) {
                        return (
                          <div className="wallet-detail">
                            {label} - {claimValue} (Claim)
                          </div>
                        );
                      }

                      return null;
                    };

                    return (
                      <>
                        {renderLimit(
                          cashlessPerTxn,
                          claimPerTxn,
                          "Per transaction limit"
                        )}
                        {renderLimit(cashlessCoPay, claimCoPay, "Copay by employee")}
                      </>
                    );
                  })()}
                </div>
              ))
            ) : (
              <p className="no-data">There is no data for sublimit</p>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box className="mt-3 wallet-section">
            <div className="filter-container">
              {" "}
              <img
                src={transactionFilter}
                alt="Filter"
                className="filter-icon"
              />{" "}
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-input"
              >
                <option value="" disabled>
                  Select filter
                </option>
                <option value="all">All transactions</option>
                <option value="3months">Last 3 months</option>
                <option value="6months">Last 6 months</option>
                <option value="1year">Last 1 year</option>
              </select>{" "}
            </div>
            {totalBlocked != 0 && (
              <div className="blocked-amount-bar">
                Blocked amount : ₹{totalBlocked?.toFixed(2)}
              </div>
            )}
            {sortedTransactions.length > 0 ? (
              <>
                {sortedTransactions.map((txn, index) => {
                  const currentMonth = moment(txn.createdAt).format("MMMM YYYY");
                  const prevMonth =
                    index > 0
                      ? moment(sortedTransactions[index - 1].createdAt).format(
                          "MMMM YYYY"
                        )
                      : null;

                  const showMonthHeader = currentMonth !== prevMonth;

                  return (
                    <React.Fragment key={txn.id}>
                      {showMonthHeader && (
                        <div className="month-divider">
                          <hr className="month-line" />
                          <span className="month-text">{currentMonth}</span>
                          <hr className="month-line" />
                        </div>
                      )}

                      <div className="wallet-transaction-card">
                        <div className="wallet-transaction-info">
                          <p className="wallet-transaction-title">
                            {txn.comment}
                          </p>
                          <p className="wallet-transaction-date">
                            {moment(txn.createdAt).format("DD MMM YYYY, hh:mm A")}
                          </p>
                        </div>

                        <div className="wallet-transaction-right">
                          <p
                            className={`wallet-transaction-amount ${
                              txn.transactionType === 1 ? "positive" : "negative"
                            }`}
                          >
                            {txn.transactionType === 1
                              ? `+₹${txn.amount}`
                              : `-₹${Math.abs(txn.amount)}`}
                          </p>

                          <div className="wallet-tags">
                            {txn.type === 2 ? (
                              <span className="wallet-tag purple">Claim</span>
                            ) : txn.type === 1 ? (
                              <span className="wallet-tag orange">Cashless</span>
                            ) : txn.type === 3 ? (
                              <span className="wallet-tag green">Refund</span>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                {txnTotalPages > 1 && (
                  <div className="wallet-pagination">
                    <button
                      className="pagination-arrow"
                      onClick={() => handleTxnPageChange(txnCurrentPage - 1)}
                      disabled={txnCurrentPage === 1}
                    >
                      ←
                    </button>

                    {[...Array(txnTotalPages)].map((_, index) => (
                      <button
                        key={index}
                        className={`pagination-page ${
                          txnCurrentPage === index + 1 ? "active" : ""
                        }`}
                        onClick={() => handleTxnPageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}

                    <button
                      className="pagination-arrow"
                      onClick={() => handleTxnPageChange(txnCurrentPage + 1)}
                      disabled={txnCurrentPage === txnTotalPages}
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="no-data">There is no transaction on this wallet</p>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box className="mt-3 wallet-section">
            {sortedBlocked.length > 0 ? (
              <>
                {sortedBlocked.map((txn, index) => {
                  const currentMonth = txn.createdAt
                    ? moment(txn.createdAt).format("MMMM YYYY")
                    : "Unknown";

                  const prevMonth =
                    index > 0 && sortedBlocked[index - 1].createdAt
                      ? moment(sortedBlocked[index - 1].createdAt).format(
                          "MMMM YYYY"
                        )
                      : null;

                  const showMonthHeader = currentMonth !== prevMonth;

                  return (
                    <React.Fragment key={txn.id}>
                      {showMonthHeader && (
                        <div className="month-divider">
                          <hr className="month-line" />
                          <span className="month-text">{currentMonth}</span>
                          <hr className="month-line" />
                        </div>
                      )}

                      <div className="wallet-transaction-card-blocked">
                        <div className="wallet-transaction-info">
                          <p className="wallet-transaction-title">
                            {txn.comment || "—"}
                          </p>
                          <p className="wallet-transaction-date">
                            {txn.createdAt
                              ? moment(txn.createdAt).format(
                                  "DD MMM YYYY, hh:mm A"
                                )
                              : "—"}
                          </p>
                        </div>

                        <div className="wallet-transaction-right">
                          <p
                            className={`wallet-transaction-amount ${
                              txn.transactionType === 1 ? "positive" : "negative"
                            }`}
                          >
                            {txn.transactionType === 1
                              ? `+₹${txn.amount}`
                              : `-₹${Math.abs(txn.amount)}`}
                          </p>

                          <div className="wallet-tags">
                            {txn.type === 2 ? (
                              <span className="wallet-tag purple">Claim</span>
                            ) : (
                              <span className="wallet-tag orange">Cashless</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                {totalPages > 1 && (
                  <div className="wallet-pagination">
                    <button
                      className="pagination-arrow"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ←
                    </button>

                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        className={`pagination-page ${
                          currentPage === index + 1 ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </button>
                    ))}

                    <button
                      className="pagination-arrow"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p className="no-data">There is no blocked amount on this wallet</p>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Blocked-amount explainer (shared across sub-limit rows) */}
      <Modal
        open={openModal}
        onClose={handleClose}
        BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.2)" } }}
      >
        <div className="wallet-info-modal">
          <div className="modal-header-wallet">
            <h3>{selectedItem.name}</h3>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          {selectedItem && (
            <>
              <div className="blocked-amount-box">
                Blocked amount : ₹
                {selectedItem?.balance?.blocked
                  ? selectedItem.balance.blocked.toFixed(2)
                  : "0.00"}
              </div>
              <div className="how-it-works">
                <h4>How this works?</h4>
                <p>
                  Blocked amount is part of your wallet balance that’s on hold
                  until verified.
                </p>
                <p>
                  The available limit = Total sublimit – (Amount spent + Amount
                  blocked)
                </p>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Dialog>
  );
}

export default WalletDetailsDialog;
